import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Check, Search, X, Plus, Bookmark, Trash2, ListFilter } from "lucide-react";
import { cn } from "@/lib/utils";
import { NO_PIPELINE } from "@/lib/pipelines";
import { useTeams } from "@/hooks/useTeams";
import { teamColor } from "@/lib/teamColors";
import PersonFaceGrid from "@/components/shared/PersonFaceGrid";
import CheckAllBar from "@/components/shared/CheckAllBar";
import type { StackPerson } from "@/components/shared/AssigneeStack";
import type { DropdownOption } from "@/types/backlog";
import type { FilterPreset } from "@/types/filterPresets";

/**
 * One person in the Added by facet.
 *
 * `profile` is what the face grid draws from and was always being passed by
 * BacklogTable — the type just stopped short of it while the facet was a list
 * of names.
 */
/** Check all / Uncheck all (#9764) under each group heading, lined up with the rows. */
const CHECK_ALL_ROW = "px-1 pb-1";

export interface PersonOption {
  id: string;
  label: string;
  profile: StackPerson;
}

interface BacklogFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  // Type, Team and Added by are the facets without a column of their own, so
  // they live behind the toolbar's one "Filter" control; everything else
  // filters from its column header.
  selectedTicketTypes: string[];
  onTicketTypesChange: (typeIds: string[]) => void;
  selectedTeams: string[];
  onTeamsChange: (teamIds: string[]) => void;
  /** Pipeline filter (#9304); NO_PIPELINE selects tickets with none. The group shows only when pipelines exist. */
  selectedPipelines: string[];
  onPipelinesChange: (pipelineIds: string[]) => void;
  pipelines: ReadonlyArray<{ id: string; name: string }>;
  /**
   * Who FILED the ticket. The facet the state has always had
   * (`selectedAddedBys` in useBacklogFilters) and the UI lost when the Added
   * By column was dropped below 2xl — so on most screens there was a live
   * filter with no way to set or clear it.
   *
   * It matters more now that Home and /focus are assigned-to-me only: "what
   * did I file?" left the dashboard, and this is where it landed.
   */
  selectedAddedBys: string[];
  onAddedBysChange: (userIds: string[]) => void;
  /** Everyone who could have filed something, for that facet's list. */
  people: PersonOption[];
  /** The reader, floated to the top of the Added by list as "Me". */
  currentUserId?: string | null;
  ticketTypes: DropdownOption[];
  /** Any filter active anywhere — header dropdowns included — shows Clear. */
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  isAuthenticated?: boolean;
  onAddItem?: () => void;
  presets?: FilterPreset[];
  onSavePreset?: (name: string) => boolean;
  onLoadPreset?: (preset: FilterPreset) => void;
  onDeletePreset?: (id: string) => void;
  viewSwitcherSlot?: React.ReactNode;
}

const BacklogFilters = ({
  searchQuery,
  onSearchChange,
  selectedTicketTypes,
  onTicketTypesChange,
  selectedTeams,
  onTeamsChange,
  selectedPipelines,
  onPipelinesChange,
  pipelines,
  selectedAddedBys,
  onAddedBysChange,
  people,
  currentUserId,
  ticketTypes,
  hasActiveFilters,
  onClearFilters,
  isAuthenticated = false,
  onAddItem,
  presets,
  onSavePreset,
  onLoadPreset,
  onDeletePreset,
  viewSwitcherSlot,
}: BacklogFiltersProps) => {
  const [presetPopoverOpen, setPresetPopoverOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);

  const moreCount =
    selectedTicketTypes.length + selectedTeams.length + selectedAddedBys.length + selectedPipelines.length;

  // The Pipeline group's rows: every pipeline, then "No pipeline".
  const pipelineOptions = [...pipelines, { id: NO_PIPELINE, name: "No pipeline" }];

  const togglePipeline = (pipelineId: string) =>
    onPipelinesChange(
      selectedPipelines.includes(pipelineId)
        ? selectedPipelines.filter((id) => id !== pipelineId)
        : [...selectedPipelines, pipelineId],
    );

  const toggleTicketType = (typeId: string) => {
    onTicketTypesChange(
      selectedTicketTypes.includes(typeId)
        ? selectedTicketTypes.filter((id) => id !== typeId)
        : [...selectedTicketTypes, typeId],
    );
  };

  const toggleTeam = (teamId: string) => {
    onTeamsChange(
      selectedTeams.includes(teamId) ? selectedTeams.filter((id) => id !== teamId) : [...selectedTeams, teamId],
    );
  };

  const toggleAddedBy = (userId: string) => {
    onAddedBysChange(
      selectedAddedBys.includes(userId)
        ? selectedAddedBys.filter((id) => id !== userId)
        : [...selectedAddedBys, userId],
    );
  };

  /**
   * The reader's face first, then everybody else alphabetically.
   *
   * "What did I file?" is the question this facet is opened for most, and
   * hunting for yourself among forty faces is the one lookup a filter should
   * never make you do. Their tip reads "Me — <name>" rather than just "Me":
   * among faces, a tip that is only a pronoun stops confirming you found the
   * right one.
   */
  const addedByOptions = useMemo(() => {
    const rest = [...people].filter((p) => p.id !== currentUserId).sort((a, b) => a.label.localeCompare(b.label));
    const me = people.find((p) => p.id === currentUserId);
    return me ? [me, ...rest] : rest;
  }, [people, currentUserId]);

  /**
   * The popover's search box drives BOTH halves of the panel.
   *
   * cmdk filters its own items from this value; the face grid is not made of
   * CommandItems (a group of them is exactly the list this facet stopped
   * being), so it has to be filtered by hand off the same string. Without
   * this, typing a name would empty the Type and Team groups and leave the
   * grid showing the whole roster.
   */
  const [filterQuery, setFilterQuery] = useState("");
  const matchedFaces = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return addedByOptions;
    // "me" finds your own face, which the name alone would not.
    return addedByOptions.filter(
      (p) => p.label.toLowerCase().includes(q) || (p.id === currentUserId && "me".startsWith(q)),
    );
  }, [addedByOptions, filterQuery, currentUserId]);

  // Teams come from the cache rather than a prop: unlike ticket types, nothing
  // above this component needs them, and threading them through BacklogTable
  // and Index would be prop-drilling for its own sake.
  const { data: teams = [] } = useTeams();
  const sortedTeams = useMemo(() => [...teams].sort((a, b) => a.name.localeCompare(b.name)), [teams]);

  const hasActiveFiltersOrSearch = !!searchQuery || hasActiveFilters;

  return (
    <div className="bg-background border-b border-border p-3 lg:p-4">
      {/* Below `sm` the bar was a COLUMN, which gave a 375px screen four stacked
          rows — add, search, the two filter buttons, the two view buttons —
          and ~180px of chrome above the first ticket. It wraps instead now:
          add and search take the first line, and the four controls fit the
          second. `max-sm:` only, so the single-line bar at every larger width
          is untouched. */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 max-sm:flex-row max-sm:flex-wrap max-sm:items-center">
        {/* Add Item Button */}
        {isAuthenticated && onAddItem && (
          <Button onClick={onAddItem} size="icon" className="shrink-0">
            <Plus className="h-4 w-4" />
          </Button>
        )}
        {/* Search */}
        <div className="relative flex-1 max-w-full sm:max-w-md max-sm:min-w-[10rem] max-sm:basis-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
          <Input
            placeholder="Search by #ID, task, description, or user..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 bg-background border-border"
          />
        </div>

        {/* Buttons row - wrap on mobile */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Presets Dropdown */}
          {presets && onSavePreset && onLoadPreset && onDeletePreset && (
            <Popover open={presetPopoverOpen} onOpenChange={setPresetPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Bookmark className={cn("h-4 w-4", presets.length > 0 && "fill-current")} />
                  <span className="hidden sm:inline">Views</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-0 bg-card border-border z-50">
                <div className="p-2 space-y-1">
                  {presets.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1">No saved views</p>}
                  {presets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center justify-between rounded-md hover:bg-secondary group"
                    >
                      <button
                        className="flex-1 text-left px-2 py-1.5 text-sm text-foreground truncate"
                        onClick={() => {
                          onLoadPreset(preset);
                          setPresetPopoverOpen(false);
                        }}
                      >
                        {preset.name}
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 dark:hover:text-red-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeletePreset(preset.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                {hasActiveFiltersOrSearch && (
                  <>
                    <div className="border-t border-border" />
                    <div className="p-2">
                      <div className="flex items-center gap-1">
                        <Input
                          placeholder="View name..."
                          value={presetName}
                          onChange={(e) => setPresetName(e.target.value)}
                          className="h-8 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && presetName.trim()) {
                              const saved = onSavePreset(presetName.trim());
                              if (saved) {
                                setPresetName("");
                              }
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          className="h-8 px-2 shrink-0"
                          disabled={!presetName.trim()}
                          onClick={() => {
                            const saved = onSavePreset(presetName.trim());
                            if (saved) {
                              setPresetName("");
                            }
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </PopoverContent>
            </Popover>
          )}

          {/* No Sort control here. Sorting is stacked now and set from the
              column headers, where the badge showing a column's position in
              the stack lives — a toolbar button naming one sort could not say
              what "Priority, then Created" is, and having both would be two
              places to look for the same answer. */}

          {/* Type + Team — the two facets that have no column header to live
              in. One small control instead of the old full filter row. */}
          <Popover open={moreOpen} onOpenChange={setMoreOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn("gap-2", moreCount > 0 && "border-[hsl(var(--ring))] text-brand")}
              >
                <ListFilter className="h-4 w-4" />
                {/* "More" said where it sat in a row of buttons, not what it
                    did. It is the only filter control left in the toolbar. */}
                <span className="hidden sm:inline">Filter</span>
                {moreCount > 0 && (
                  <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold leading-4 text-primary-foreground">
                    {moreCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            {/* w-72, not w-60: the Added by grid wants seven 28px faces plus
                gutters, and 60 (240px) only fits five. */}
            <PopoverContent align="start" className="w-72 p-0 bg-card border-border z-50">
              <Command className="bg-card">
                <CommandInput
                  placeholder="Filter by type, pipeline, team or filer..."
                  className="text-foreground"
                  value={filterQuery}
                  onValueChange={setFilterQuery}
                />
                {/* Only mounted when the FACES have nothing either — cmdk shows
                    this whenever its own items are all filtered out, which is
                    true of every search for a person's name. Unmounted, a
                    face-only match leaves the command half silently empty
                    instead of contradicting the grid below it. */}
                {matchedFaces.length === 0 && (
                  <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">No match.</CommandEmpty>
                )}
                <CommandGroup heading="Type" className="bg-card">
                  <CheckAllBar
                    checked={ticketTypes.filter((t) => selectedTicketTypes.includes(t.id)).length}
                    total={ticketTypes.length}
                    onChange={(on) => onTicketTypesChange(on ? ticketTypes.map((t) => t.id) : [])}
                    className={CHECK_ALL_ROW}
                  />
                  {ticketTypes.map((type) => (
                    <CommandItem
                      key={type.id}
                      value={`type ${type.label}`}
                      onSelect={() => toggleTicketType(type.id)}
                      className="text-foreground"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0 text-brand",
                          selectedTicketTypes.includes(type.id) ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="mr-2 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: type.color }} />
                      <span className="truncate">{type.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                {pipelines.length > 0 && (
                  <CommandGroup heading="Pipeline" className="bg-card">
                    <CheckAllBar
                      checked={pipelineOptions.filter((p) => selectedPipelines.includes(p.id)).length}
                      total={pipelineOptions.length}
                      onChange={(on) => onPipelinesChange(on ? pipelineOptions.map((p) => p.id) : [])}
                      className={CHECK_ALL_ROW}
                    />
                    {pipelineOptions.map((pipeline) => (
                      <CommandItem
                        key={pipeline.id}
                        value={`pipeline ${pipeline.name}`}
                        onSelect={() => togglePipeline(pipeline.id)}
                        className="text-foreground"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 shrink-0 text-brand",
                            selectedPipelines.includes(pipeline.id) ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className={cn("truncate", pipeline.id === NO_PIPELINE && "text-muted-foreground")}>
                          {pipeline.name}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                <CommandGroup heading="Team" className="max-h-48 overflow-y-auto bg-card">
                  <CheckAllBar
                    checked={sortedTeams.filter((t) => selectedTeams.includes(t.id)).length}
                    total={sortedTeams.length}
                    onChange={(on) => onTeamsChange(on ? sortedTeams.map((t) => t.id) : [])}
                    className={CHECK_ALL_ROW}
                  />
                  {sortedTeams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={`team ${team.name}`}
                      onSelect={() => toggleTeam(team.id)}
                      className="text-foreground"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0 text-brand",
                          selectedTeams.includes(team.id) ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span
                        className="mr-2 h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: teamColor(team) }}
                      />
                      <span className="truncate">{team.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
              {/* Added by — the third facet with no column to live in, as a
                  grid of faces (see PersonFaceGrid for the full reasoning).
                  It sits outside the <Command> because cmdk only manages its
                  own items, but it still answers the search box: matchedFaces
                  is the roster filtered by the same query, so typing a name
                  narrows the faces exactly like it narrows the Type rows. */}
              {matchedFaces.length > 0 && (
                <div className="border-t border-border px-3 pb-3 pt-2">
                  <p className="pb-2 text-xs font-medium text-muted-foreground">Added by</p>
                  {/* The scroll box is a PADDED WRAPPER, not the grid itself.
                      `overflow-y: auto` forces `overflow-x` to auto as well, so
                      a grid that scrolls is also a grid that CLIPS — and a
                      selected face paints a 2px ring plus a check badge that
                      overhang its own box. On the grid, the right-hand column's
                      ring would be shaved and a horizontal scrollbar would
                      appear the moment anyone picked someone. The p-1 gives
                      those 2px somewhere to land inside the clip box. */}
                  <div className="max-h-40 overflow-y-auto p-1">
                    <PersonFaceGrid
                      people={matchedFaces}
                      selected={selectedAddedBys}
                      onToggle={toggleAddedBy}
                      currentUserId={currentUserId}
                      ariaLabel="Filter by who filed the ticket"
                    />
                  </div>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Clear — shows once anything is filtering, wherever it was set */}
          {hasActiveFiltersOrSearch && (
            <Button variant="ghost" size="sm" onClick={onClearFilters} className="gap-2 text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">Clear Filters</span>
            </Button>
          )}
        </div>

        {/* The view controls — Highlight and Group — as a pair at the right
            end: both decide how the rows on screen read, not which rows there
            are. `flex-wrap` so a narrow window drops one under the other
            rather than pushing them off the bar. */}
        {viewSwitcherSlot && (
          <div className="flex flex-wrap items-center gap-2 max-sm:min-w-0 sm:ml-auto">{viewSwitcherSlot}</div>
        )}
      </div>
    </div>
  );
};

export default BacklogFilters;
